/**
 * imagen-recorte.js — el ESTILO de la imagen de un bloque (pedido del dueño, D-1429, 26-10-2026).
 *
 * «Tarjeta» es lo de siempre: esquinas redondeadas, sombra de CAJA y la foto llenando su caja
 * (`object-fit: cover`). Con un PNG de fondo transparente (una mascota, un producto recortado, un
 * logo) eso delata el rectángulo —la sombra va alrededor de la caja, no de la figura— y el
 * `cover` le corta la cabeza. «Recorte» quita el marco (sin radio ni sombra de caja), enseña la
 * imagen ENTERA (`contain`) y, si se pide, le pone una sombra que sigue la SILUETA
 * (`filter: drop-shadow`).
 *
 * El dato: `image_mode: 'recorte'` y `image_silueta: true`. Sin ellos el bloque sale byte a byte
 * como siempre (los renders solo añaden clases cuando hay prop). No están en el esquema del LLM:
 * «Mejorar con IA» los conserva (`ai-merge.js`), como los del tamaño a mano.
 *
 * PURO (node, ES2019): lo usan los renders (content, hero v3, features, imagen y, por item, logos), la barra (render.js),
 * el panel (editor/imagen-estilo.js) y el selector de imagen al subir (catalog.js +
 * imagen-comprimir.js, que mide el borde con `esSiluetaRecortada`).
 */

export var PROP_MODO = 'image_mode';
export var PROP_SILUETA = 'image_silueta';
export var MODO_RECORTE = 'recorte';
export var PROPS_RECORTE = [PROP_MODO, PROP_SILUETA];

/** Por debajo de este alfa (0-255) un píxel del borde cuenta como transparente. */
export var ALFA_TRANSPARENTE = 32;
/** Fracción del perímetro transparente a partir de la cual la imagen es un recorte. Una foto con
 *  esquinas redondeadas se queda en ~10 %; una figura recortada, por encima del 60 %. */
export var BORDE_MIN = 0.3;

/**
 * ¿Este bloque pinta una imagen a la que se le puede quitar el marco? Las de FONDO (hero v1/v2,
 * banner) no: no tienen marco. El vídeo tampoco. El bloque `imagen` (D-1452), las dos variantes
 * (a sangre y en marco), en cuanto tiene una foto; no mira `use_image`, que ese bloque no usa.
 */
export function admiteRecorte(tipo, props) {
    var p = props || {};
    if (tipo === 'imagen') return !!(p.image_url || p.image_keyword);
    if (!p.use_image) return false;
    if (tipo === 'content' || tipo === 'features') return true;
    if (tipo === 'hero') {
        var video = p.video_url && p.video_url !== 'placeholder';
        return p.variant === 'v3' && !video;
    }
    return false;
}

/**
 * Los bloques con una imagen POR ITEM que admite el recorte (D-1452: «Recorte para logos PNG
 * transparentes»): tipo → el array de props. Marcas y Catálogo comparten render y datos
 * (`blocks/logos.js`), así que van juntos. El dato vive en el ITEM (`logos[i].image_mode`).
 */
export var RECORTE_POR_ITEM = { logos: 'logos', catalogo: 'logos' };

/** ¿Este item del array `array` de un bloque `tipo` admite el recorte? (Necesita su imagen.) */
export function admiteRecorteDeItem(tipo, array, item) {
    if (!Object.prototype.hasOwnProperty.call(RECORTE_POR_ITEM, tipo) || RECORTE_POR_ITEM[tipo] !== array) return false;
    return !!(item && typeof item === 'object' && typeof item.image === 'string' && item.image);
}

/** {recorte, silueta} leídos del dato (los props del bloque o un item). La silueta solo cuenta en modo recorte. */
export function modoImagen(props) {
    var p = props || {};
    var recorte = p[PROP_MODO] === MODO_RECORTE;
    return { recorte: recorte, silueta: recorte && p[PROP_SILUETA] === true };
}

/** Clases para el `<img>` (con espacio delante, o '' sin prop: el markup de siempre). */
export function clasesImagenRecorte(props) {
    var m = modoImagen(props);
    if (!m.recorte) return '';
    return ' n3i-recorte' + (m.silueta ? ' n3i-recorte-silueta' : '');
}

/** Clase para la CAJA que pinta el marco por su cuenta (la del hero v3), o ''. */
export function claseMarcoRecorte(props) {
    return modoImagen(props).recorte ? ' n3i-recorte-marco' : '';
}

/**
 * Los cambios de props para pasar a un modo (formato de `guardarPropsDeBloque`: `null` borra).
 * «Tarjeta» BORRA los dos props: la ausencia es lo de siempre.
 */
export function cambiosDeModo(recorte, silueta) {
    var c = {};
    c[PROP_MODO] = recorte ? MODO_RECORTE : null;
    c[PROP_SILUETA] = recorte && silueta ? true : null;
    return c;
}

/**
 * ¿Es una figura recortada? Mira el PERÍMETRO de una muestra RGBA (`ImageData.data` de un lienzo
 * pequeño): si al menos `BORDE_MIN` de sus píxeles son transparentes, el fondo es transparente.
 * Una imagen opaca —o con alfa solo dentro, o en las esquinas— no lo es.
 */
export function esSiluetaRecortada(rgba, ancho, alto) {
    if (!rgba || !(ancho > 1) || !(alto > 1) || rgba.length < ancho * alto * 4) return false;
    var total = 0;
    var claros = 0;
    var mira = function (x, y) {
        total += 1;
        if (rgba[(y * ancho + x) * 4 + 3] < ALFA_TRANSPARENTE) claros += 1;
    };
    var x, y;
    for (x = 0; x < ancho; x++) { mira(x, 0); mira(x, alto - 1); }
    for (y = 1; y < alto - 1; y++) { mira(0, y); mira(ancho - 1, y); }
    return total > 0 && claros / total >= BORDE_MIN;
}
